import { Divider } from '@material-ui/core'; // Using this Divider since new OS 3.0 does not have it
import { forwardRef } from 'react';
import { Box, Button, Icon, IconButton, OsModalProps, Typography } from '../../core';
import { styled } from '../../styles';
import { BaseThemeType, useMediaQuery } from '../../theme';
import { getModalMaxWidth, getModalSize } from '../../utils/commonFunctions';

type StyledModalContentProps = {
  modalType: OsModalProps['modalType']
}

export const StyledModalHeader = styled('div')<StyledModalContentProps>(({ modalType, theme }) => ({
  padding: '20px 20px 16px 20px',
  [theme.breakpoints.down(393)]: {
    padding: '16px',
  },
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: modalType === 'onboarding' ? 'column-reverse' : 'row',
}))

export const StyledModalHeaderWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}))

export const StyledModalTitleWrapper = styled('div')<StyledModalContentProps>(({ modalType, theme }) => ({
  alignSelf: modalType === 'onboarding' ? 'center' : 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  textAlign: modalType === 'onboarding' ? 'center' : 'left',
}))

export const StyledModalCloseIconWrapper = styled('div')<StyledModalContentProps>(({ modalType, theme }) => ({
  alignSelf: modalType === 'onboarding' ? 'flex-end' : 'auto',
}))

const StyledModalContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  padding: 20,
  [theme.breakpoints.down(393)]: {
    padding: '16px',
  },
  minHeight: 120,
  overflowY: 'hidden',
  height: '100%',
}))

const StyledModalContent = styled('div')(({ theme }) => ({
  width: '100%',
  overflowY: 'auto',
}))

const StyledModalDivider = styled(Divider)(({ theme }) => ({
  backgroundColor: theme.palette.neutralGrey[700],
}))

export const StyledModalFooter = styled('div')<StyledModalContentProps>(({ modalType, theme }) => ({
  padding: '16px 20px 20px 20px',
  [theme.breakpoints.down(393)]: {
    padding: '16px',
  },
  display: 'flex',
  gap: 12,
  justifyContent: modalType === 'onboarding' ? 'center' : 'space-between',
}))

const StyledModalFooterRightButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 12,
}))

const StyledModalShadow = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 81,
  width: '100%',
  height: 56,
  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.60) 47.66%, #FFF 115.17%)',
}))

export const ModalContent = forwardRef<HTMLDivElement, OsModalProps>((props, ref) => {
  const {
    headerTitle,
    headerSubtitle,
    modalContent,
    acceptButtonProps,
    cancelButtonProps,
    tertiaryButtonProps,
    acceptButtonLabel = 'Accept',
    cancelButtonLabel = 'Cancel',
    tertiaryButtonLabel,
    onClickAcceptButton = () => { },
    onClickCancelButton = () => { },
    onClickTertiaryButton = () => { },
    onClickCloseButton = () => { },
    size = 'xSmall',
    modalType = 'default',
  } = props

  const isDesktop = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.up('lg'))
  const isTablet = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.between(393, 'lg'))
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down(393))
  const isTabletOrMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('lg'))

  const modalSize = getModalSize(size, isMobile, modalType)

  return (
    <Box
      // @ts-ignore
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        width: modalSize === 'medium' && isMobile ? '100%' : '90%',
        maxWidth: getModalMaxWidth(modalSize, isDesktop, isTablet, isMobile),
        height: modalSize === 'medium' && isMobile ? '100%' : 'auto',
        maxHeight: modalSize === 'medium' && isMobile ? '100%' : '80vh',
        borderRadius: modalSize === 'medium' && isMobile ? 0 : 8,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <StyledModalHeaderWrapper>
        <StyledModalHeader modalType={modalType}>
          <StyledModalTitleWrapper modalType={modalType}>
            <Typography
              sx={{ color: (theme) => `${theme.palette.neutralGrey[1300]} !important` }}
              variant={isTabletOrMobile ? 'body1SemiBold' : 'h5'}
            >
              {headerTitle}
            </Typography>
            <Typography
              sx={{ color: (theme) => `${theme.palette.neutralGrey[1000]} !important` }}
              variant={isTabletOrMobile ? 'body3' : 'body1'}
            >
              {headerSubtitle}
            </Typography>
          </StyledModalTitleWrapper>
          <StyledModalCloseIconWrapper modalType={modalType}>
            <IconButton
              sx={{ color: (theme) => `${theme.palette.neutralGrey[900]} !important`, width: 24, height: 24 }}
              size={'medium'}
              onClick={onClickCloseButton}
            >
              <Icon icon="close" />
            </IconButton>
          </StyledModalCloseIconWrapper>
        </StyledModalHeader>
        {modalType === 'default' && <StyledModalDivider />}
      </StyledModalHeaderWrapper>
      <StyledModalContentWrapper>
        <StyledModalContent>{modalContent}</StyledModalContent>
      </StyledModalContentWrapper>
      <div>
        <StyledModalShadow></StyledModalShadow>
        {modalType === 'default' && <StyledModalDivider />}
        <StyledModalFooter modalType={modalType}>
          <div>
            {tertiaryButtonLabel ||
              (tertiaryButtonProps && modalType !== 'onboarding' && (
                <Button
                  {...tertiaryButtonProps}
                  size={isMobile ? 'small' : 'medium'}
                  variant="text"
                  onClick={onClickTertiaryButton}
                >
                  {tertiaryButtonLabel}
                </Button>
              ))}
          </div>
          <StyledModalFooterRightButtonsWrapper>
            <Button
              {...cancelButtonProps}
              size={isMobile ? 'small' : 'medium'}
              variant="outlined"
              onClick={onClickCancelButton}
            >
              {cancelButtonLabel}
            </Button>
            <Button
              {...acceptButtonProps}
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              color="secondary"
              onClick={onClickAcceptButton}
            >
              {acceptButtonLabel}
            </Button>
          </StyledModalFooterRightButtonsWrapper>
        </StyledModalFooter>
      </div>
    </Box>
  )
})
